import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { leaveAPI, employeeAPI } from '../services/api';
import DetailedLeaveBalance from './DetailedLeaveBalance';

interface LeaveRequest {
  id: number;
  request_type: 'ferie' | 'permesso' | 'malattia' | 'altri';
  start_date: string;
  end_date?: string;
  duration_days?: number;
  duration_hours?: number;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  notes?: string;
  request_date: string;
  approval_date?: string;
  approver_notes?: string;
}

interface LeaveBalance {
  ferie_totali: number;
  ferie_usate: number;
  ferie_residue: number;
  permessi_totali: number;
  permessi_usate: number;
  permessi_residue: number;
  giorni_malattia: number;
}

const LeaveManagement: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [balance, setBalance] = useState<LeaveBalance | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dailyHours, setDailyHours] = useState<number>(8); // Ore giornaliere dal contratto

  // Form state
  const [requestType, setRequestType] = useState<'ferie' | 'permesso' | 'malattia' | 'altri'>('ferie');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [durationHours, setDurationHours] = useState<number>(0);
  const [leaveReason, setLeaveReason] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadData();
    loadDailyHours();
  }, []);

  const loadDailyHours = async () => {
    try {
      const data = await employeeAPI.getMyEmployee();
      const emp = data.employee;
      if (emp?.ore_settimanali && emp?.giorni_settimanali && emp.giorni_settimanali > 0) {
        setDailyHours(emp.ore_settimanali / emp.giorni_settimanali);
      }
    } catch {
      // Mantieni default 8
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [requestsRes, balanceRes] = await Promise.all([
        leaveAPI.getMyRequests(),
        leaveAPI.getBalance(),
      ]);
      setRequests(requestsRes.requests || []);
      setBalance(balanceRes);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!startDate) {
      setError('Data inizio obbligatoria');
      return;
    }

    if (requestType !== 'permesso' && !endDate) {
      setError('Data fine obbligatoria per questo tipo di richiesta');
      return;
    }

    // Validazione: per i permessi, la causale è obbligatoria
    if (requestType === 'permesso' && !leaveReason) {
      setError('La causale è obbligatoria per i permessi');
      return;
    }

    if (requestType === 'permesso' && durationHours <= 0) {
      setError('Inserisci le ore del permesso');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestData: any = {
        request_type: requestType,
        start_date: startDate,
        notes: notes || undefined,
      };

      if (endDate) {
        requestData.end_date = endDate;
      }

      if (requestType === 'permesso') {
        requestData.duration_hours = durationHours;
        requestData.leave_reason = leaveReason;
      }

      await leaveAPI.createLeaveRequest(requestData);

      setSuccess('Richiesta creata con successo');
      setOpenDialog(false);
      resetForm();
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nella creazione della richiesta');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setRequestType('ferie');
    setStartDate('');
    setEndDate('');
    setDurationHours(0);
    setLeaveReason('');
    setNotes('');
  };

  const getStatusChip = (status: string) => {
    const colors: Record<string, 'default' | 'success' | 'error' | 'warning'> = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
      cancelled: 'default',
    };

    const labels: Record<string, string> = {
      pending: 'In Attesa',
      approved: 'Approvata',
      rejected: 'Rifiutata',
      cancelled: 'Annullata',
    };

    return <Chip label={labels[status]} color={colors[status]} size="small" />;
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      ferie: 'Ferie',
      permesso: 'Permesso',
      malattia: 'Malattia',
      altri: 'Altri',
    };
    return labels[type] || type;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Gestione Ferie e Permessi
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{ backgroundColor: '#0d47a1', '&:hover': { backgroundColor: '#1565c0' } }}
        >
          Nuova Richiesta
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Saldo Ferie/Permessi Dettagliato */}
      <Box sx={{ mb: 3 }}>
        <DetailedLeaveBalance />
      </Box>

      {/* Lista Richieste */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Le Mie Richieste
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#E3F2FD' }}>
                  <TableCell sx={{ fontWeight: 'bold', color: '#0d47a1' }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#0d47a1' }}>Periodo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#0d47a1' }}>Durata</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#0d47a1' }}>Stato</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#0d47a1' }}>Data Richiesta</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#0d47a1' }}>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        Nessuna richiesta presente
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((req) => (
                    <TableRow key={req.id}>
                      <TableCell>{getTypeLabel(req.request_type)}</TableCell>
                      <TableCell>
                        {new Date(req.start_date).toLocaleDateString('it-IT')}
                        {req.end_date && ` - ${new Date(req.end_date).toLocaleDateString('it-IT')}`}
                      </TableCell>
                      <TableCell>
                        {req.duration_days
                          ? `${req.duration_days.toFixed(1)} giorni`
                          : req.duration_hours
                          ? `${req.duration_hours.toFixed(1)} ore`
                          : '-'}
                      </TableCell>
                      <TableCell>{getStatusChip(req.status)}</TableCell>
                      <TableCell>{new Date(req.request_date).toLocaleDateString('it-IT')}</TableCell>
                      <TableCell>{req.notes || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog Nuova Richiesta */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuova Richiesta di Ferie/Permessi</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              select
              label="Tipo Richiesta"
              value={requestType}
              onChange={(e) => setRequestType(e.target.value as any)}
              fullWidth
            >
              <MenuItem value="ferie">Ferie</MenuItem>
              <MenuItem value="permesso">Permesso</MenuItem>
              <MenuItem value="malattia">Malattia</MenuItem>
              <MenuItem value="altri">Altri</MenuItem>
            </TextField>

            <TextField
              type="date"
              label="Data Inizio"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />

            {requestType !== 'permesso' && (
              <TextField
                type="date"
                label="Data Fine"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
            )}

            {requestType === 'permesso' && (
              <>
                <TextField
                  type="number"
                  label={`Durata (ore) - max ${dailyHours.toFixed(1)}h giornaliere`}
                  value={durationHours}
                  onChange={(e) => setDurationHours(parseFloat(e.target.value) || 0)}
                  fullWidth
                  inputProps={{ min: 0, step: 0.5, max: dailyHours }}
                  helperText={`Ore giornaliere del tuo contratto: ${dailyHours.toFixed(1)}h`}
                  required
                />
                <FormControl fullWidth required>
                  <InputLabel>Causale</InputLabel>
                  <Select
                    value={leaveReason}
                    label="Causale"
                    onChange={(e) => setLeaveReason(e.target.value)}
                  >
                    <MenuItem value="Studio">Studio</MenuItem>
                    <MenuItem value="ROL (Riduzione Orario Lavorativo)">ROL (Riduzione Orario Lavorativo)</MenuItem>
                    <MenuItem value="Lutto">Lutto</MenuItem>
                    <MenuItem value="Matrimonio">Matrimonio</MenuItem>
                    <MenuItem value="Malattia del figlio">Malattia del figlio</MenuItem>
                    <MenuItem value="Esami">Esami</MenuItem>
                    <MenuItem value="Visite mediche">Visite mediche</MenuItem>
                    <MenuItem value="Motivi personali">Motivi personali</MenuItem>
                    <MenuItem value="Donazione sangue">Donazione sangue</MenuItem>
                    <MenuItem value="Accompagnamento familiare">Accompagnamento familiare</MenuItem>
                    <MenuItem value="Adozione/Affidamento">Adozione/Affidamento</MenuItem>
                    <MenuItem value="Congedo parentale">Congedo parentale</MenuItem>
                    <MenuItem value="Altro">Altro</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            <TextField
              multiline
              rows={3}
              label="Note (opzionale)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Annulla</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading || (requestType === 'permesso' && (!leaveReason || durationHours <= 0))}
          >
            Invia Richiesta
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveManagement;
